/**
 * External Imports
 */
import { FC, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../redux/store';

/**
 * Internal Imports
 */

import EmployeeSideNav from '../../components/sideNav/EmployeeSideNav';
import SearchHeader from '../../components/searchHeader';
import EmployeesTable from '../../components/customTables/employees/employeeListTable';

import { useGetRoundingListConfigQuery } from '../../redux/apis/Users.api';

import { fireSnow } from '../../services/Confetti';
import {
  isPercentLessThanOne,
  isPercentRoundedToOneHundred
} from '../../utils/complianceFunctions';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Employee } from '../../config/interfaces';

/**
 * Style Imports
 */
import { EmployeeContainer } from './styles/Employee.styles';

export interface EmployeeListProps {
  authorizedUser?: AuthUserProps;
  employeesToRound: Employee[];
  searchTerm: string;
  selectedDelegate: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const EmployeeList: FC<EmployeeListProps> = ({
  authorizedUser,
  employeesToRound,
  searchTerm,
  selectedDelegate,
  setSearchTerm
}) => {
  const [selectedTab, setSelectedTab] = useState<string>('All');
  const [rendered, setRendered] = useState(false);

  const {
    data: roundingListConfig = {}
    // isError: isGetRoundingListConfigError,
    // isFetching: isGetRoundingListConfigFetching,
    // isLoading: isGetRoundingListConfigLoading
  } = useGetRoundingListConfigQuery(selectedDelegate, {
    refetchOnMountOrArgChange: 30,
    skip: !(selectedDelegate && selectedDelegate.length === 7)
  });

  // TODO: Convert to memo
  const roundsCompleted: Array<Employee> = [];
  const roundsToDo: Array<Employee> = [];
  const newHireToDos: Array<Employee> = [];
  const newHireCompleted: Array<Employee> = [];

  employeesToRound.forEach(employee => {
    if (employee.complianceStatus === 'Completed') {
      if (employee.newHire) {
        newHireCompleted.push(employee);
      }
      roundsCompleted.push(employee);
    } else {
      if (employee.newHire) {
        newHireToDos.push(employee);
      }
      roundsToDo.push(employee);
    }
  });

  // TODO: Convert to memo
  const completePercent = isPercentLessThanOne(
    roundsCompleted.length,
    employeesToRound.filter(x => x.effectiveStartDate).length
  )
    ? 1
    : isPercentRoundedToOneHundred(
        roundsCompleted.length,
        employeesToRound.filter(x => x.effectiveStartDate).length
      )
    ? 99
    : Math.round(
        (roundsCompleted.length /
          employeesToRound.filter(x => x.effectiveStartDate).length) *
          100
      ) || 0;

  const todoPercent =
    employeesToRound.filter(x => x.effectiveStartDate).length === 0
      ? 0
      : 100 - completePercent;

  let roundingFrequency;
  if (roundingListConfig.listRoundingFrequency) {
    roundingFrequency = roundingListConfig.listRoundingFrequency;
  } else {
    roundingFrequency =
      authorizedUser?.employeeInfo &&
      authorizedUser?.employeeInfo?.myRoundingListConfig &&
      authorizedUser?.employeeInfo?.myRoundingListConfig.listRoundingFrequency;
  }

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (
      window.sessionStorage.getItem('droppedEmployeeSnow') &&
      employeesToRound.length > 0 &&
      roundsToDo.length > 0
    ) {
      window.sessionStorage.removeItem('droppedEmployeeSnow');
    }
  }, [employeesToRound, roundsToDo]);

  useEffect(() => {
    if (
      rendered &&
      !window.sessionStorage.getItem('droppedEmployeeSnow') &&
      employeesToRound.length > 0 &&
      completePercent === 100
    ) {
      setTimeout(() => {
        fireSnow(
          4,
          1,
          { x: 0.16, y: 0.5 },
          [-0.4, 0.4],
          [0.4, 0.8],
          [0.4, 1],
          ['circle'],
          ['#a8a9ad']
        );
        fireSnow(
          4,
          1,
          { x: 0.16, y: 0.5 },
          [-0.4, 0.4],
          [0.4, 0.8],
          [0.4, 1],
          ['circle'],
          ['##e05a29']
        );
        fireSnow(
          4,
          1,
          { x: 0.16, y: 0.5 },
          [-0.4, 0.4],
          [0.4, 0.8],
          [0.4, 1],
          ['circle'],
          ['#206da3']
        );
      }, 1000);
      window.sessionStorage.setItem('droppedEmployeeSnow', 'true');
    }
  }, [employeesToRound, completePercent]);

  return (
    <EmployeeContainer id="Employee-Container">
      <EmployeeSideNav
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        completeCount={roundsCompleted.length}
        completePercent={completePercent}
        todoPercent={todoPercent}
        todoCount={roundsToDo.filter(x => x.effectiveStartDate).length}
        totalCount={employeesToRound.filter(x => x.effectiveStartDate).length}
        newHires={newHireToDos.length + newHireCompleted.length}
        unableCount={0}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <EmployeesTable
          searchTerm={searchTerm}
          selectedTab={selectedTab}
          newHireToDo={newHireToDos}
          newHireComplete={newHireCompleted}
          completedRounds={roundsCompleted}
          toDoRounds={roundsToDo}
          frequency={roundingFrequency}
        />
      </div>
    </EmployeeContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    employeesToRound: state.EmployeeReducer.employeesToRound,
    selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee.hcaid
  };
};

export default connect(mapReduxStateToProps)(EmployeeList);
